import {
  type EmbedMessages,
  EN_EMBED_MESSAGES,
  loadEmbedMessages,
  peekEmbedMessages,
} from "./embed-messages";
import type { SupportedLocale } from "./locales";
import { loadMessages, type MessagesBundle } from "./runtime-locale";

type Load = (locale: SupportedLocale) => Promise<MessagesBundle>;

export interface EmbedLocaleController {
  /**
   * Messages to render for `locale` RIGHT NOW: the locale itself once loaded, else what is already
   * on screen (bundled English on the first paint, the previous language on a switch). Starts the
   * load once per requested locale; `onChange` fires when the answer changes.
   */
  request(locale: SupportedLocale): EmbedMessages;
}

/**
 * Imperative twin of `useEmbedMessages` for the web component (a class, no hooks): render first,
 * swap when the locale chunk lands, never wait on the network.
 */
export function createEmbedLocaleController(
  onChange: () => void,
  load: Load = loadMessages,
): EmbedLocaleController {
  let shown: EmbedMessages = EN_EMBED_MESSAGES;
  let requested: SupportedLocale | null = null;
  return {
    request(locale) {
      const ready = peekEmbedMessages(locale);
      if (ready) {
        shown = ready;
        requested = locale;
      } else if (requested !== locale) {
        // One request per requested locale: a failed load (resolves to English) is not re-asked on
        // every render; asking for another locale and back retries.
        requested = locale;
        void loadEmbedMessages(locale, load).then((next) => {
          if (requested !== locale) return; // stale response
          shown = next;
          onChange();
        });
      }
      return shown;
    },
  };
}
